/**
 * 差分検知機能の型定義
 */

import type { ColumnId, DatasetId, ModelId, WorkspaceId, ReportId } from '../../../types/id.types';
import type { DataValue, ColumnName, DataType } from '../../../types/core.types';

// ============================================================================
// セル・行レベルの変更
// ============================================================================

/**
 * セル単位の変更
 */
export interface CellChange {
  /** __system_rowid__の値 */
  rowNumber: number;
  /** 変更があった列のID */
  columnId: ColumnId;
  /** 変更前の値 */
  oldValue: DataValue;
  /** 変更後の値 */
  newValue: DataValue;
}

/**
 * 除外行の変更
 */
export interface ExclusionChange {
  /** 変更タイプ */
  type: 'excluded' | 'unexcluded';
  /** RowNumber */
  rowNumber: number;
}

/**
 * コメントの変更
 */
export interface CommentChange {
  /** 変更タイプ */
  type: 'added' | 'removed' | 'modified';
  /** RowNumber */
  rowNumber: number;
  /** 変更前のコメント */
  oldComment?: string;
  /** 変更後のコメント */
  newComment?: string;
}

/**
 * メタデータの変更
 */
export interface MetadataChange {
  /** 変更されたフィールド名 */
  field: string;
  /** 変更前の値 */
  oldValue?: string;
  /** 変更後の値 */
  newValue?: string;
}

// ============================================================================
// スキーマ（列定義）の変更
// ============================================================================

/**
 * 列の変更タイプ
 */
export type ColumnChangeType = 'added' | 'removed' | 'renamed' | 'typeChanged' | 'scaleChanged';

/**
 * 列の変更
 */
export interface ColumnChange {
  /** 変更タイプ */
  type: ColumnChangeType;
  /** 列ID */
  columnId: ColumnId;
  /** 変更前の列名（renamed時） */
  oldName?: ColumnName;
  /** 変更後の列名（renamed時） */
  newName?: ColumnName;
  /** 変更前のデータ型（typeChanged時） */
  oldType?: DataType;
  /** 変更後のデータ型（typeChanged時） */
  newType?: DataType;
}

/**
 * スキーマの変更
 */
export interface SchemaChange {
  /** 列の変更リスト */
  columnChanges: ColumnChange[];
}

// ============================================================================
// PrimaryDataSetの差分
// ============================================================================

/**
 * PrimaryDataSetの詳細な差分
 */
export interface PrimaryDataSetDiff {
  /** データセットID */
  datasetId: DatasetId;
  /** 差分があるか */
  hasDifferences: boolean;

  // データの変更
  /** セル単位の変更リスト */
  cellChanges: CellChange[];
  /** 追加された行のRowNumber */
  rowsAdded: number[];
  /** 削除された行のRowNumber */
  rowsDeleted: number[];

  // スキーマの変更
  /** スキーマ変更 */
  schemaChanges: SchemaChange;

  // メタデータの変更
  /** メタデータが変更されたか */
  metadataChanged: boolean;
  /** メタデータの詳細変更 */
  metadataChanges: MetadataChange[];
  /** データセット名が変更されたか */
  nameChanged: boolean;
  /** 変更前の名前 */
  oldName?: string;
  /** 変更後の名前 */
  newName?: string;

  // 除外行・コメントの変更
  /** 除外行が変更されたか */
  exclusionsChanged: boolean;
  /** 除外行の詳細変更 */
  exclusionChanges: ExclusionChange[];
  /** コメントが変更されたか */
  commentsChanged: boolean;
  /** コメントの詳細変更 */
  commentChanges: CommentChange[];

  // 大規模変更で詳細が省略された場合
  /** 変更が多すぎて詳細が省略されたか */
  _truncated?: boolean;
  /** 省略されたセル変更数 */
  _truncatedCellChanges?: number;
}

// ============================================================================
// レポートの差分
// ============================================================================

/**
 * Markdown本文の行単位の変更
 */
export interface ContentLineDiff {
  /** 変更タイプ */
  type: 'added' | 'removed' | 'unchanged';
  /** 行番号（表示用） */
  lineNumber: number;
  /** 行の内容 */
  content: string;
}

/**
 * Markdown本文の差分
 */
export interface ContentDiff {
  /** 変更があるか */
  hasChanges: boolean;
  /** 差分行（変更周辺のコンテキスト付き） */
  lines: ContentLineDiff[];
  /** 追加された行数 */
  addedLines: number;
  /** 削除された行数 */
  removedLines: number;
}

/**
 * プロパティの変更
 */
export interface PropertyChange {
  /** プロパティパス（例: 'graphConfig.layers[0].geom'） */
  property: string;
  /** 変更前の値 */
  oldValue?: unknown;
  /** 変更後の値 */
  newValue?: unknown;
}

/**
 * レポート要素の変更
 */
export interface ReportElementChange {
  /** 要素ID */
  elementId: string;
  /** 変更タイプ */
  type: 'added' | 'removed' | 'modified';
  /** 要素の種類（'graph_builder', 'crosstab'等） */
  elementType: string;
  /** 要素名（titleがあれば） */
  elementName?: string;
  /** modified時の詳細変更 */
  propertyChanges?: PropertyChange[];
}

/**
 * レポートの詳細差分
 */
export interface ReportDiff {
  /** レポートID */
  reportId: ReportId;
  /** レポート名 */
  reportName: string;
  /** 差分があるか */
  hasDifferences: boolean;

  // 基本情報の変更
  /** 名前が変更されたか */
  nameChanged: boolean;
  /** 変更前の名前 */
  oldName?: string;
  /** 変更後の名前 */
  newName?: string;
  /** 説明が変更されたか */
  descriptionChanged: boolean;
  /** 変更前の説明 */
  oldDescription?: string;
  /** 変更後の説明 */
  newDescription?: string;

  // Markdown本文の差分
  /** コンテンツ差分 */
  contentDiff: ContentDiff;

  // 要素の変更
  /** 要素の変更リスト */
  elementChanges: ReportElementChange[];

  // 設定の変更
  /** 設定が変更されたか */
  settingsChanged: boolean;
  /** 設定の詳細変更 */
  settingsChanges?: PropertyChange[];
}

// ============================================================================
// 簡易差分（変更有無のみ）
// ============================================================================

/**
 * 簡易差分（変更の有無のみ）
 */
export interface SimpleDiff {
  /** 差分があるか */
  hasDifferences: boolean;
  /** 変更があったパスのリスト（デバッグ用） */
  changedPaths?: string[];
}

// ============================================================================
// プロジェクト全体の差分
// ============================================================================

/**
 * プロジェクト全体の差分
 */
export interface ProjectDiff {
  /** 差分があるか */
  hasDifferences: boolean;

  // PrimaryDataSetの詳細差分
  primaryDatasets: {
    /** 追加されたデータセットID */
    added: DatasetId[];
    /** 削除されたデータセットID */
    removed: DatasetId[];
    /** 変更されたデータセットの詳細差分 */
    modified: Record<DatasetId, PrimaryDataSetDiff>;
  };

  // DerivedDataSetの簡易差分
  derivedDatasets: {
    /** 追加されたデータセットID */
    added: DatasetId[];
    /** 削除されたデータセットID */
    removed: DatasetId[];
    /** 変更されたデータセットID */
    modified: DatasetId[];
  };

  // その他の変更（有無のみ）
  models: SimpleDiff & {
    /** 追加されたモデルID */
    added: ModelId[];
    /** 削除されたモデルID */
    removed: ModelId[];
    /** 変更されたモデルID */
    modified: ModelId[];
  };

  reports: {
    /** 差分があるか */
    hasDifferences: boolean;
    /** 追加されたレポートID */
    added: ReportId[];
    /** 削除されたレポートID */
    removed: ReportId[];
    /** 変更されたレポートの詳細差分 */
    modified: Record<ReportId, ReportDiff>;
  };

  workspaces: SimpleDiff & {
    /** 追加されたワークスペースID */
    added: WorkspaceId[];
    /** 削除されたワークスペースID */
    removed: WorkspaceId[];
    /** 変更されたワークスペースID */
    modified: WorkspaceId[];
  };

  /** プロジェクト状態の差分 */
  projectState: SimpleDiff;
  /** メタデータの差分 */
  metadata: SimpleDiff;
}

// ============================================================================
// 差分オプション
// ============================================================================

/**
 * 差分計算オプション
 */
export interface DiffOptions {
  /** 最大セル変更数（デフォルト: 1000） */
  maxCellChanges?: number;
  /** 比較する最大行数（デフォルト: 100000） */
  maxRowsToCompare?: number;
  /** 大規模変更時に詳細を省略するか（デフォルト: true） */
  truncateOnLargeChange?: boolean;
}

/**
 * デフォルトの差分オプション
 */
export const DEFAULT_DIFF_OPTIONS: Required<DiffOptions> = {
  maxCellChanges: 1000,
  maxRowsToCompare: 100000,
  truncateOnLargeChange: true,
};
